import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from "@iconify/react";

export default function SeatSetupModel({ open, onClose, onSave, capacity }) {
    const [rows, setRows] = useState(1);
    const [columns, setColumns] = useState(1);
    const [disabledSeats, setDisabledSeats] = useState([]);
    const [error, setError] = useState('');
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const validateSeatCount = (updatedRows = rows, updatedCols = columns, updatedDisabledSeats = disabledSeats) => {
        const totalSeats = updatedRows * updatedCols - updatedDisabledSeats.length;

        if (totalSeats > capacity) {
            setError(`Seat count exceeds capacity ${capacity}. You're trying to create ${totalSeats} seats.`);
        } else {
            setError('');
        }
    };

    // Convert number to alphabetic row name (1 -> A, 2 -> B, etc.)
    const getRowName = (rowIndex) => {
        // Handle up to 26 rows (A-Z)
        return String.fromCharCode(65 + rowIndex);
    };

    const toggleSeatSelection = (seatId) => {
        let updatedSeats;
        if (disabledSeats.includes(seatId)) {
            updatedSeats = disabledSeats.filter(id => id !== seatId);
        } else {
            updatedSeats = [...disabledSeats, seatId];
        }
        setDisabledSeats(updatedSeats);
        validateSeatCount(rows, columns, updatedSeats);
    };

    // const handleSave = () => {
    //     const layout = [];

    //     for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
    //         const rowName = getRowName(rowIndex); // A, B, ...
    //         const rowNumber = rowIndex + 1;

    //         for (let colIndex = 0; colIndex < columns; colIndex++) {
    //             const columnNumber = colIndex + 1;
    //             const seatId = `${rowName}${columnNumber}`;
    //             if (disabledSeats.includes(seatId)) continue; // Skip if the seat is disabled
    //             // Add seat to layout if it's not disabled
    //             layout.push({
    //                 name: seatId,
    //                 row: rowNumber,
    //                 column: columnNumber,
    //             });
    //         }
    //     }

    //     onSave && onSave({ rows, columns, seats: layout });
    //     onClose();
    //     setColumns(1);
    //     setRows(1);
    //     setDisabledSeats([]);
    // };
    const handleSave = () => {
        const totalSeats = rows * columns - disabledSeats.length;

        if (totalSeats > capacity) {
            setError(`You can only create up to ${capacity} seats. You currently have ${totalSeats}.`);
            return;
        }
        setError(''); 
        const layout = [];

        for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
            const rowName = getRowName(rowIndex); // A, B, ...
            const rowNumber = rowIndex + 1;

            for (let colIndex = 0; colIndex < columns; colIndex++) {
                const columnNumber = colIndex + 1;
                const seatId = `${rowName}${columnNumber}`;
                if (disabledSeats.includes(seatId)) continue; // Skip if disabled

                layout.push({
                    name: seatId,
                    row: rowNumber,
                    column: columnNumber,
                });
            }
        }

        onSave && onSave({ rows, columns, seats: layout });
        onClose();
        setColumns(1);
        setRows(1);
        setDisabledSeats([]);
    };

    const isRowDisabled = (rowIndex) => {
        const rowName = getRowName(rowIndex);
        for (let col = 1; col <= columns; col++) {
            const seatId = `${rowName}${col}`;
            if (!disabledSeats.includes(seatId)) return false;
        }
        return true;
    };
    const toggleRow = (rowIndex) => {
        const rowName = getRowName(rowIndex);
        const seatIds = [];
        for (let col = 1; col <= columns; col++) {
            seatIds.push(`${rowName}${col}`);
        }

        setDisabledSeats((prev) => {
            const isDisabled = seatIds.every(id => prev.includes(id));
            const updated = isDisabled
                ? prev.filter(id => !seatIds.includes(id))
                : [...new Set([...prev, ...seatIds])];

            validateSeatCount(rows, columns, updated);
            return updated;
        });

    };

    const isColumnDisabled = (colIndex) => {
        const colNumber = colIndex + 1;
        for (let row = 0; row < rows; row++) {
            const rowName = getRowName(row);
            const seatId = `${rowName}${colNumber}`;
            if (!disabledSeats.includes(seatId)) return false;
        }
        return true;
    };

    const toggleColumn = (colIndex) => {
        const colNumber = colIndex + 1;
        const seatIds = [];
        for (let row = 0; row < rows; row++) {
            const rowName = getRowName(row);
            seatIds.push(`${rowName}${colNumber}`);
        }

        setDisabledSeats((prev) => {
            const isDisabled = seatIds.every(id => prev.includes(id));
            const updated = isDisabled
                ? prev.filter(id => !seatIds.includes(id))
                : [...new Set([...prev, ...seatIds])];

            validateSeatCount(rows, columns, updated);
            return updated;
        });

    };

    const renderSeats = () => {
        const seatLayout = [];

        // Row labels at top (column numbers)
        const headerRow = [
            <div key="empty-corner" className="w-8 h-8"></div> // Empty corner cell
        ];

        // Add column numbers directly above their columns
        for (let col = 1; col <= columns; col++) {
            headerRow.push(
                <div key={`col-${col}`} className="w-8 h-8 m-0.5 flex flex-col items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">{col}</span>
                    <button
                        className={`text-xs mt-1 ${isColumnDisabled(col - 1) ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleColumn(col - 1);
                        }}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                        </svg>
                    </button>
                </div>
            );
        }

        // Add extra cell at the end of header row (to match the right row label)
        headerRow.push(
            <div key="empty-corner-right" className="w-8 h-8"></div>
        );

        seatLayout.push(
            <div key="column-labels" className="flex mb-2">
                {headerRow}
            </div>
        );

        // Generate seats row by row
        for (let row = 0; row < rows; row++) {
            const rowName = getRowName(row);
            const rowCells = [];

            // Row label at left
            rowCells.push(
                <div key={`row-${rowName}`} className="w-8 h-8 flex items-center justify-center font-medium text-gray-600">
                    {rowName}
                    <button
                        className={`ml-1 ${isRowDisabled(row) ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(row);
                        }}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                        </svg>
                    </button>
                </div>
            );

            // Seats in the row
            for (let col = 1; col <= columns; col++) {
                const seatId = `${rowName}${col}`;
                const isSelected = disabledSeats.includes(seatId);

                rowCells.push(
                    <div
                        key={seatId}
                        className={`w-8 h-8 m-0.5 flex items-center justify-center rounded cursor-pointer text-xs transition-colors
                        ${isSelected ? 'bg-gray-100 text-gray-500' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'}`}
                        onClick={() => toggleSeatSelection(seatId)}
                    >
                        {seatId}
                    </div>
                );
            }

            // Row label at right (mirroring left side)
            rowCells.push(
                <div key={`row-${rowName}-right`} className="w-8 h-8 flex items-center justify-center font-medium text-gray-600">
                    {rowName}
                </div>
            );

            seatLayout.push(
                <div key={`row-${row}`} className="flex items-center mb-1">
                    {rowCells}
                </div>
            );
        }

        return seatLayout;
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div
                ref={modalRef}
                className="bg-white rounded-lg shadow-xl w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto"
            >
                <div className="p-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800">Seat Layout Setup</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>

                <div className="p-6">
                    <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-center">
                        <div className="flex flex-col">
                            <label htmlFor="rows" className="mb-1 text-sm font-medium text-gray-700">Total Rows</label>
                            <input
                                id="rows"
                                type="number"
                                min="1"
                                max="26"
                                value={rows}
                                // onChange={(e) => setRows(Math.min(26, Math.max(1, parseInt(e.target.value) || 1)))}
                                onChange={(e) => {
                                    const newRow = Math.min(26, Math.max(1, parseInt(e.target.value) || 1));
                                    setRows(newRow);
                                    validateSeatCount(newRow, columns, disabledSeats);
                                }}
                                className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="columns" className="mb-1 text-sm font-medium text-gray-700">Total Seats Per Row (Columns)</label>
                            <input
                                id="columns"
                                type="number"
                                min="1"
                                max="50"
                                value={columns}
                                // onChange={(e) => setColumns(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                                onChange={(e) => {
                                    const newCol = Math.min(50, Math.max(1, parseInt(e.target.value) || 1));
                                    setColumns(newCol);
                                    validateSeatCount(rows, newCol, disabledSeats);
                                }}
                                className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div className="overflow-x-auto pb-4 max-h-[50vh]">
                        <div className="min-w-max">
                            <div className="seat-layout flex flex-col items-center">
                                {renderSeats()}
                            </div>
                        </div>
                    </div>
                    {/* {disabledSeats.length > 0 && (
                        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                            <h2 className="font-medium mb-2">Selected Seats: {disabledSeats.length}</h2>
                            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                                {disabledSeats.map(seat => (
                                    <span key={seat} className="px-2 py-1 bg-blue-600 text-white rounded text-sm">
                                        {seat}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )} */}
                    {error && (
                        <div className="flex justify-center text-sm text-red-700">
                            {error}
                        </div>
                    )}
                </div>

                <div className="border-t p-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!!error}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        Save Layout
                    </button>
                </div>
            </div>
        </div>
    );
}

SeatSetupModel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    capacity: PropTypes.number.isRequired,
    permissionId: PropTypes.string,
    onSave: PropTypes.func.isRequired,
};